import React, { Suspense, useRef, useState } from 'react';
import Link from '@ff/ui/Link';
import copylink from './icons/icon_copylink.svg';
import email from './icons/icon_email.svg';
import facebook from './icons/icon_facebook.svg';
import instagram from './icons/icon_instagram.svg';
import linkedin from './icons/icon_linkedin.svg';
import reddit from './icons/icon_reddit.svg';
import twitter from './icons/icon_twitter.svg';
import wechat from './icons/icon_wechat.svg';
import weibo from './icons/icon_weibo.svg';
import whatsapp from './icons/icon_whatsapp.svg';
import Box from '@ff/ui/Box';
import { styled } from '@ff/ui';
import { isMobile } from 'shared/lib/useragentdetection';

const QRCode = React.lazy(() => import(/* webpackChunkName: "qrcode" */ 'qrcode.react'));

export interface Props {
  icon?: string;
  metadata?: {
    url: string;
    title: string;
    description: string;
  }
  region?: string;
}

export default function SocialIcon(props: Props) {
  const { icon, metadata, region } = props;
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const timeoutRef = useRef();

  const handleCopyLink = async (text: string) => {
    // Copy URL to clipboard and show success message
    const promise = await navigator?.clipboard?.writeText(text);
    if (promise !== null) {
      setShowCopySuccess(true);
      setTimeout(() => {
        setShowCopySuccess(false);
      }, 2000);
      // Clear success message
      clearTimeout(timeoutRef.current);
    }
  }

  if (['wecom', 'wechat'].includes(icon)) {
    return (
      <IconWrapper>
        <Centered>
          <QROverlayWrapper url={metadata?.url}>
            <img src={getIcon(icon)} alt={icon} />
          </QROverlayWrapper>
        </Centered>
        <Centered>
          <QROverlayWrapper url={metadata?.url}>
            { getIconLabel(icon, region) }
          </QROverlayWrapper>
        </Centered>
      </IconWrapper>
    )
  }

  if (icon === 'copylink') {
    const copiedText = region === 'US' ? 'Copied' : '已复制';

    return (
      <IconWrapper>
        <Centered>
          <img
            src={getIcon(icon)}
            alt={icon}
            onClick={() => { handleCopyLink(metadata?.url); }}
          />
        </Centered>
        <Centered onClick={() => { handleCopyLink(metadata?.url); }}>
          {showCopySuccess ? copiedText : getIconLabel(icon, region)}
        </Centered>
      </IconWrapper>
    );
  }

  return (
    <IconWrapper key={icon}>
      <CenteredLink onClick={getIconHandler(icon, metadata)}>
        <img src={getIcon(icon)} alt={icon} />
      </CenteredLink>
      <CenteredLink onClick={getIconHandler(icon, metadata)}>
        { getIconLabel(icon, region) }
      </CenteredLink>
    </IconWrapper>
  )
}

interface QRIconProps {
  url: string;
  children: React.ReactNode;
}

function QROverlayWrapper(props: QRIconProps) {
  const { url, children } = props;

  const [isOpen, setIsOpen] = useState(false);

  return (
    <Box
      position="relative"
      onMouseEnter={() => { setIsOpen(true); }}
      onMouseLeave={() => { setIsOpen(false); }}
    >
      { children }
      {
        isOpen &&
        <>
          <QRWrapper>
            <QRBorder>
              <Mobile>
                <Suspense fallback={<div />}>
                  <QRCode
                    size={50}
                    value={url}
                  />
                </Suspense>
              </Mobile>
              <Desktop>
                <Suspense fallback={<div />}>
                  <QRCode
                    size={112}
                    value={url}
                  />
                </Suspense>
              </Desktop>
            </QRBorder>
          </QRWrapper>
          <Triangle />
        </>
      }
    </Box>
  )
}

const IconWrapper = styled(Box)({
  cursor: 'pointer',
  display: 'flex',
  gap: '10px',
  borderRadius: '3px',
  padding: '5px',
  WebkitTapHighlightColor: 'transparent',
});

const Centered = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

const CenteredLink = styled(Link)({
  display: 'flex',
  alignItems: 'center',
  WebkitTapHighlightColor: 'transparent',
});

const Mobile = styled(Box)(({ theme }) => ({
  [theme.mediaQueries.md]: {
    display: 'none',
  }
}));

const Desktop = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.mediaQueries.md]: {
    display: 'block',
  }
}));

const QRWrapper = styled(Box)(({ theme }) => ({
  position: 'absolute',
  zIndex: '3',
  backgroundColor: 'rgba(217, 217, 217, 0.4)',

  top: '-71px',
  left: 'calc((-64px + 100%) / 2)',
  padding: '3px',

  [theme.mediaQueries.md]: {
    top: '-177px',
    left: 'calc((-144px + 100%) / 2)',
    padding: '6px',
  }
}));

const QRBorder = styled(Box)(({ theme }) => ({
  backgroundColor: 'white',
  // Workaround for mysterious 4px below the QR code
  padding: "4px 4px 0px 4px",

  [theme.mediaQueries.md]: {
    // Workaround for mysterious 4px below the QR code
    padding: "10px 10px 6px 10px",
  }
}));

const Triangle = styled(Box)(({ theme }) => ({
  position: "absolute",
  zIndex: "3",
  width: "0",
  height: "0",

  top: "-7px",
  left: '4px',
  borderLeft: "6px solid transparent",
  borderRight: "6px solid transparent",
  borderTop: "6px solid rgba(217, 217, 217, 0.4)",

  [theme.mediaQueries.md]: {
    top: "-33px",
    left: '-50%',
    borderLeft: "20px solid transparent",
    borderRight: "20px solid transparent",
    borderTop: "20px solid rgba(217, 217, 217, 0.4)",
  }
}));


function getIcon(icon: string) {
  switch (icon) {
    case 'copylink':
      return copylink;
    case 'email':
      return email;
    case 'facebook':
      return facebook;
    case 'instagram':
      return instagram;
    case 'linkedin':
      return linkedin;
    case 'reddit':
      return reddit;
    case 'twitter':
      return twitter;
    case 'wechat':
      return wechat;
    case 'weibo':
      return weibo;
    case 'whatsapp':
      return whatsapp;
  }
}

function getIconLabel(icon: string, region: string) {
  const copyLink = region === 'US' ? 'Copy Link' : '复制链接';

  switch (icon) {
    case 'copylink':
      return copyLink;
    case 'email':
      return 'Email';
    case 'facebook':
      return 'Facebook';
    case 'instagram':
      return 'Instagram';
    case 'linkedin':
      return 'LinkedIn';
    case 'reddit':
      return 'Reddit';
    case 'twitter':
      return 'Twitter';
    case 'wechat':
      return '微信';
    case 'weibo':
      return '微博';
    case 'whatsapp':
      return 'WhatsApp';
  }
}

function getIconHandler(icon: string, metadata: Props['metadata']) {
  switch (icon) {
    case 'copylink':
      return () => {
        navigator?.clipboard?.writeText(metadata?.url);
      };
    case 'email':
      return () => {
        window.open(`mailto:?subject=${metadata?.description}&body=${metadata?.url}`, '_blank');
      };
    case 'facebook':
      return () => {
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${metadata?.url}`, '_blank');
      };
    // Instagram currently has no way to share.
    // case 'instagram':
    //   return () => {};
    case 'linkedin':
      return () => {
        window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${metadata?.url}&title=${metadata?.title}&summary=${metadata?.description}`, '_blank');
      };
    case 'reddit':
      return () => {
        window.open(`https://www.reddit.com/r/technology/submit?title=${metadata?.title}&url=${metadata?.url}`, '_blank');
      };
    case 'twitter':
      return () => {
        if (isMobile()) {
          window.open(`twitter://post?message=${metadata?.title}%20@FaradayFuture%20${metadata?.url}`);
        } else {
          window.open(`https://twitter.com/share?url=${metadata?.url}&text=${metadata?.title}%20@FaradayFuture`, '_blank');
        }
      };
    case 'weibo':
      return () => {
        window.open(`http://service.weibo.com/share/share.php?url=${metadata?.url}&title=${metadata?.title}%20@FaradayFuture&appkey=1343713053&pic=&searchPic=false`, '_blank');
      };
    case 'whatsapp':
      return () => {
        if (isMobile()) {
          window.open(`whatsapp://send?text=${metadata?.description}%20${metadata?.url}`, '_blank');
        } else {
          window.open(`https://web.whatsapp.com/send?text=${metadata?.description}%20${metadata?.url}`, '_blank');
        }
      };
  }

  return () => {
    window.open(metadata.url, '_blank');
  };
}
